import { ShiftManagementSelectableShiftsRowsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import ShiftsRow, {
  ShiftsRowType,
  TShiftsRow,
} from 'modules/shift_management/store/internal/models/ShiftsRow'

interface Result {
  fetch: (params: { endAt?: string; startAt?: string }) => Promise<ShiftsRow[]>
  loading: boolean
}

export const useSelectableShiftsRows = (): Result => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasLegacyJobCatalogFeature = hasFeatureFlag('legacy_job_catalog')
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  // const currentPage = filters.statusId === SHIFT_CONFLICTS ? null : page
  const startAtWithPreviousDay = moment(view.startAt).subtract(1, 'day').format('YYYY-MM-DD')
  const selectableEmployees = useSelectableEmployees(
    startAtWithPreviousDay,
    view.endAt,
    'read',
    filters.locationIds
  )
  const { loading: selectableEmployeesLoading } = selectableEmployees

  const employeeIds = useUnsafeMemo(() => {
    if (!filters.terminated) return filters.employeeIds

    const requestedIds = filters.employeeIds
    return requestedIds && requestedIds.length > 0 ? requestedIds : null
  }, [filters.employeeIds, filters.terminated])

  const [fetchEmployees, { loading: employeesLoading }] = useLazyQuery(
    ShiftManagementSelectableShiftsRowsDocument,
    { fetchPolicy: 'cache-first' }
  )

  const fetch = async (params: { endAt?: string; startAt?: string }): Promise<ShiftsRow[]> => {
    const startAt = params.startAt || startAtWithPreviousDay
    const endAt = params.endAt || view.endAt
    const { data: employeesData } = await fetchEmployees({
      variables: {
        contractOnDate: view.startAt,
        orderBy: localStorageOrderEmployeesBy,
        orderDir: localStorageOrderEmployeesDir,
        // page: currentPage,
        // perPage: MAX_ITEMS_PER_PAGE,
        customFilters: filters.customFilters,
        // - Accept employeeIds to refresh some isolated rows on update/delete
        employeeIds,
        endAt,
        endAtISO: endAt,
        logicOperator: LOGIC_OPERATOR.AND,
        shiftIds: filters.shiftIds,
        withoutShiftIds: filters.withoutShiftIds,
        startAt,
        startAtISO: startAt,
        terminated: filters.terminated,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        legalEntityIds: filters.legalEntityIds,
      },
      fetchPolicy: 'cache-first',
    })

    return (
      employeesData?.shiftManagement?.employeesPermissions.map((shiftsRow: TShiftsRow) =>
        ShiftsRow.buildFromGraphQL(shiftsRow, ShiftsRowType.employee, hasLegacyJobCatalogFeature)
      ) || []
    )
  }

  return { fetch, loading: selectableEmployeesLoading || employeesLoading }
}
